import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, TrendingDown, Save, ArrowLeft, Receipt, CreditCard } from 'lucide-react';
import { toast } from 'react-toastify';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useAuthStore } from '../../stores/authStore';
import { useExpenseTypes, useActiveAccounts, useCreateExpense } from '../../hooks/useQueries';
import { printExpenseReceipt } from '../../lib/printReceipt';
import { getLastFormDefault, setLastFormDefault } from '../../lib/formDefaults';
import { getErrorMessage } from '../../lib/utils/errorMessage';

// Electron API is defined in preload.ts - using type assertion for compatibility

interface FormData {
  name: string;
  amount: string;
  expenseTypeId: string;
  accountId: string;
}

const AddExpense: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // Use React Query hooks
  const { data: expenseTypesData = [], isLoading: expenseTypesLoading } = useExpenseTypes();
  const { data: accountsData = [], isLoading: accountsLoading } = useActiveAccounts();
  
  const expenseTypes = Array.isArray(expenseTypesData) ? expenseTypesData.filter((et: any) => et.isActive) : [];
  const accounts = Array.isArray(accountsData) ? accountsData : [];
  const dataLoading = expenseTypesLoading || accountsLoading;
  
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    amount: '',
    expenseTypeId: '',
    accountId: ''
  });

  // Create expense mutation
  const createExpenseMutation = useCreateExpense({
    onSuccess: async (data: any) => {
      const expenseTypeName =
        expenseTypes.find((t: any) => t._id === formData.expenseTypeId)?.name || data?.expenseTypeName || '';
      const accountName =
        accounts.find((a: any) => a._id === formData.accountId)?.name || data?.accountName || '';
      if (formData.expenseTypeId) {
        setLastFormDefault('lastExpenseTypeId', formData.expenseTypeId);
      }
      await printExpenseReceipt({
        expenseId: data?.expenseId ?? data?._id ?? '—',
        name: data?.name ?? formData.name,
        amount: data?.amount ?? parseFloat(formData.amount),
        expenseTypeName,
        accountName,
        userName: data?.userName ?? user?.name,
      });
      navigate('/expenses');
    },
    onError: (error: any) => {
      console.error('Error creating expense:', error);
      toast.error(getErrorMessage(error, 'Failed to create expense'));
      setLoading(false);
    }
  });

  // Set default account to cash account if available
  useEffect(() => {
    if (accounts.length > 0 && !formData.accountId) {
      const cashAccount = accounts.find((acc: any) => acc.isCashAccount);
      if (cashAccount) {
        setFormData(prev => ({ ...prev, accountId: cashAccount._id }));
      }
    }
  }, [accounts, formData.accountId]);

  useEffect(() => {
    if (expenseTypes.length === 0) return;
    const lastTypeId = getLastFormDefault('lastExpenseTypeId');
    if (lastTypeId && expenseTypes.some((t: any) => t._id === lastTypeId)) {
      setFormData((prev) =>
        prev.expenseTypeId ? prev : { ...prev, expenseTypeId: lastTypeId }
      );
    }
  }, [expenseTypes]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Guard against rapid double-submits (double-click / slow network).
    if (loading || createExpenseMutation.isLoading) {
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Description is required');
      return;
    }
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    
    if (!formData.expenseTypeId) {
      toast.error('Expense type is required');
      return;
    }

    if (!formData.accountId) {
      toast.error('Account is required');
      return;
    }

    if (expenseTypes.length === 0) {
      toast.error('No expense types available. Please contact an administrator.');
      return;
    }

    if (accounts.length === 0) {
      toast.error('No accounts available. Please contact an administrator.');
      return;
    }

    setLoading(true);
    const expenseData = {
      name: formData.name.trim(),
      amount: parseFloat(formData.amount),
      expenseTypeId: formData.expenseTypeId,
      accountId: formData.accountId
    };
    
    createExpenseMutation.mutate(expenseData);
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (dataLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
              <TrendingDown className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Add Expense</h1>
              <p className="text-gray-600 dark:text-gray-400">Record organizational expenses for Central Office</p>
            
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Expense Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  Description *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Construction Material"
                  required
                />
              </div>
              
              <div>
                <label className="form-label">
                  Amount *
                </label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  placeholder="0"
                  min="0"
                  step="1"
                  required
                />
              </div>
            </div>
          </div>

          {/* Classification */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Classification</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  Expense Type *
                </label>
                <select
                  value={formData.expenseTypeId}
                  onChange={(e) => handleInputChange('expenseTypeId', e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select expense type</option>
                  {expenseTypes && expenseTypes.length > 0 ? expenseTypes.map((type) => (
                    <option key={type._id} value={type._id}>
                      {type.name}
                    </option>
                  )) : (
                    <option value="" disabled>Loading expense types...</option>
                  )}
                </select>
              </div>
              
              <div>
                <label className="form-label">
                  Account *
                </label>
                <select
                  value={formData.accountId}
                  onChange={(e) => handleInputChange('accountId', e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select account</option>
                  {accounts && accounts.length > 0 ? accounts.map((account) => (
                    <option key={account._id} value={account._id}>
                      {account.name} ({account.accountNumber}) - Rs. {account.balance.toLocaleString()}
                    </option>
                  )) : (
                    <option value="" disabled>Loading accounts...</option>
                  )}
                </select>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Default: Cash Account (CASH01)
                </p>
              </div>
            </div>
          </div>

          {/* Transaction Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Transaction Details</h3>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">User:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{user?.name || 'Unknown'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Role:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">{user?.role || 'Unknown'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Date:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">
                    {new Date().toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Time:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">
                    {new Date().toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/expenses')}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary"
              disabled={loading}
              leftIcon={<Save className="h-4 w-4" />}
            >
              {loading ? 'Creating...' : 'Create Expense'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddExpense;
